
import { computed, defineComponent, provide, ref, watch } from 'vue'
import GraphView from '@/components/graph/GraphView.vue'
import GraphCirculation from '@/components/graph/GraphCirculation.vue'
import { useGraph } from '@/composable/useGraph'
import { useApp } from '@/composable/useApp'
import DeviationDelayMPKModal from '@/components/deviation/deviation-delay/DeviationDelayMPKModal.vue'
import DeviationDelayMPKMultipleModal from '@/components/deviation/deviation-delay/DeviationDelayMPKMultipleModal.vue'
import { useModal } from '@/composable/useModal'
import { getMpkEvent } from '@/api/mpk/getMpkEvent'
import { searchDelay } from '@/api/search/searchDelay'
import { searchPrevTripFromTraind } from '@/api/search/prevTripFromTraind'
import DeviationDelayModal from '@/components/deviation/DeviationDelayModal.vue'
import { useGraphCirculation } from '@/composable/useGraphCirculation'
import { MpkEventMessageData } from '@/api/graph/getMpkEventMessage'
import { useRoute } from 'vue-router'
import type { QSelectProps } from 'quasar'

export default defineComponent({
  components: {
    GraphView,
    GraphCirculation,
    DeviationDelayMPKModal,
    DeviationDelayModal,
    DeviationDelayMPKMultipleModal,
  },

  setup() {
    const opalModal = useModal()
    const mpkModal = useModal()
    const delayModal = useModal()
    provide('opal-modal', opalModal)
    provide('delay-mpk-modal', mpkModal)
    provide('delay-modal-2', delayModal)
    const {
      model,
      events,
      showCirculations,
      timelineZoomMode,
      grafMode,
      showOpal,
      simulated,
      loading: loadingGraph,
      refresh,
      setSelectedTrainNumber,
      activeSearchMode,
      placeSearchHours,
    } = useGraph()
    const { loading: loadingCirculation } = useGraphCirculation()
    const { showHeader } = useApp()
    const route = useRoute()

    const tempTrainNumber = ref(model.value.trainNumber)
    const tempPlaceSearchModal = ref({
      from: model.value.from,
      to: model.value.to,
    })

    watch(
      () => model.value.trainNumber,
      (value) => {
        tempTrainNumber.value = value
      }
    )

    if (
      typeof route.query.from === 'string' &&
      typeof route.query.to === 'string'
    ) {
      model.value.from = route.query.from
      model.value.to = route.query.to
      tempPlaceSearchModal.value.from = route.query.from
      tempPlaceSearchModal.value.to = route.query.to
    }

    if (typeof route.query.via === 'string' && route.query.via.length) {
      model.value.via = route.query.via.split(',')
    }

    const onSearchTrainNumber = () => {
      model.value.trainNumber = tempTrainNumber.value
      model.value.from = tempPlaceSearchModal.value.from
      model.value.to = tempPlaceSearchModal.value.to
    }

    const onDepartureDateChange = (v: string) => {
      if (v.length !== 10) return
      model.value.departureDate = v
    }

    const toggleShowHeader = () => {
      showHeader.value = !showHeader.value
    }

    events.mpk_cb = (mpk_event_id: string) => {
      const eventId = mpk_event_id.split(' ')[0].replace('H', '')
      mpkModal?.openModal({
        cb: async (setData) => {
          await getMpkEvent(eventId)
            .then(({ data }) => {
              setData({
                eventId,
                mpkData: data,
              })
            })
            .catch((error) => {
              setData(error.response.data.message)
            })

          return Promise.resolve()
        },
      })
    }

    events.opal_cb = (mpkMessageData: MpkEventMessageData[]) => {
      opalModal?.openModal({
        cb: async (setData) => {
          setData({
            mpkMessageData,
          })
        },
      })
    }

    events.delay_cb = (advertised: string, departureDate: string) => {
      delayModal?.openModal({
        cb: async (setData) => {
          const params = {
            technical: advertised,
            departureDate: departureDate,
          }
          await Promise.all([
            searchDelay(params),
            searchPrevTripFromTraind({
              tnr: parseInt(params.technical, 10),
              date: params.departureDate,
            }),
          ])
            .then(([{ data }, { data: prevTripFromTraind }]) => {
              setData({
                data,
                prevTripFromTraind,
                technical: params.technical,
                departure: params.departureDate,
              })
            })
            .catch((error) => {
              setData(error.response.data.message)
            })

          return Promise.resolve()
        },
      })
    }

    const loading = computed(() => {
      return loadingGraph.value || loadingCirculation.value
    })

    const refreshLoading = ref(false)

    const onRefresh = async () => {
      refreshLoading.value = true
      await refresh()
      refreshLoading.value = false
    }

    watch(placeSearchHours, () => {
      setSelectedTrainNumber(model.value.trainNumber, model.value.departureDate)
    })

    watch(activeSearchMode, () => {
      setSelectedTrainNumber(model.value.trainNumber, model.value.departureDate)
    })

    const createViaValue: QSelectProps['onNewValue'] = (val, done) => {
      if (val.length > 0) {
        done(val.toLowerCase(), 'add-unique')
      }
    }

    const toggleLocation = () => {
      const from = tempPlaceSearchModal.value.from
      const to = tempPlaceSearchModal.value.to
      tempPlaceSearchModal.value.from = to
      tempPlaceSearchModal.value.to = from
      onSearchTrainNumber()
    }

    return {
      onSearchTrainNumber,
      tempTrainNumber,
      model,
      onDepartureDateChange,
      toggleShowHeader,
      showHeader,
      mpkModal,
      delayModal,
      showCirculations,
      timelineZoomMode,
      grafMode,
      loading,
      opalModal,
      showOpal,
      simulated,
      onRefresh,
      refreshLoading,
      activeSearchMode,
      tempPlaceSearchModal,
      placeSearchHours,
      createViaValue,
      toggleLocation,
    }
  },
})
